import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Plus, Trash2, Settings, Image } from 'lucide-react';
import { EDTable, EDWaiter } from '../../types/EventDay';
import { eventDayApi } from '../../services/EventDayApiClient';
import { getVenues, Venue } from '../../services/VenueService';
import { buildTablePayload, canLinkFloorPlans } from './tableFloorPlan';
import { groupFloorPlansByVenue, venueNameFor } from './floorPlanGroups';
import { getFloorPlans, createFloorPlan, deleteFloorPlan, FloorPlan } from '../../services/FloorPlanService';
import { resolveMediaUrl } from '../../services/uploadService';
import { loadVenuesFromApi } from '../../services/VenueApiService';
import { syncTablesFromFloorPlans } from '../../services/EventDaySync';
import TagMultiSelect from '../ui/TagMultiSelect';
import SearchableSelect from '../ui/SearchableSelect';
import { useNotifications } from '../NotificationProvider';
import DataTable, { Column } from '../DataTable';
import EmptyState from '../EmptyState';
import LoadErrorBox from '../ui/LoadErrorBox';

type SetupTab = 'tables' | 'floor-plans' | 'waiters';

interface Props {
  onClose: () => void;
  onSaved: () => void;
  inline?: boolean;
  visibleTabs?: SetupTab[];
}

interface TableForm {
  number: string;
  name: string;
  section: string;
  capacity: string;
  floor_plan_id: string;
}

const EMPTY_FORM: TableForm = { number: '', name: '', section: '', capacity: '', floor_plan_id: '' };

interface WaiterForm {
  name: string;
  pin: string;
  phone: string;
  role: 'waiter' | 'usher' | 'bouncer' | 'other';
}

const EMPTY_WAITER_FORM: WaiterForm = { name: '', pin: '', phone: '', role: 'waiter' };

interface FloorPlanForm {
  name: string;
  venue_id: string;
}
const EMPTY_FP_FORM: FloorPlanForm = { name: '', venue_id: '' };

const TableSetupModal: React.FC<Props> = ({ onClose, onSaved, inline = false, visibleTabs }) => {
  const { showError } = useNotifications();
  const tabs: SetupTab[] = visibleTabs ?? ['tables', 'floor-plans', 'waiters'];
  const [activeTab, setActiveTab] = useState<SetupTab>(tabs[0]);
  const [tables, setTables] = useState<EDTable[]>([]);
  const [waiters, setWaiters] = useState<EDWaiter[]>([]);
  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [waiterTableMap, setWaiterTableMap] = useState<Record<string, Set<string>>>({});
  const [form, setForm] = useState<TableForm>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [assignSaving, setAssignSaving] = useState<string | null>(null);
  const [waiterForm, setWaiterForm] = useState<WaiterForm>(EMPTY_WAITER_FORM);
  const [waiterSaving, setWaiterSaving] = useState(false);
  const [waiterDeleting, setWaiterDeleting] = useState<string | null>(null);
  const [fpForm, setFpForm] = useState<FloorPlanForm>(EMPTY_FP_FORM);
  const [fpSaving, setFpSaving] = useState(false);
  const [fpDeleting, setFpDeleting] = useState<string | null>(null);
  const [fpImageFile, setFpImageFile] = useState<File | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [fpZoneNames, setFpZoneNames] = useState<string[]>([]);
  const fpImageRef = useRef<HTMLInputElement>(null);

  const [loadError, setLoadError] = useState<unknown>(null);

  const loadData = useCallback(async () => {
    try {
      setLoadError(null);
      const [tData, wData] = await Promise.all([
        eventDayApi.getTables(),
        eventDayApi.getWaiters(),
      ]);
      const loadedTables: EDTable[] = tData.tables || [];
      const loadedWaiters: EDWaiter[] = wData.waiters || [];
      setTables(loadedTables);
      setWaiters(loadedWaiters);

      const map: Record<string, Set<string>> = {};
      await Promise.all(loadedWaiters.map(async w => {
        try {
          const { tables: wt } = await eventDayApi.getWaiterTables(w.id);
          map[w.id] = new Set((wt || []).map((t: any) => t.id as string));
        } catch {
          map[w.id] = new Set();
        }
      }));
      setWaiterTableMap(map);
    } catch (err) {
      // Stays on screen with a Retry (a toast would vanish and leave empty lists).
      setLoadError(err ?? new Error('load failed'));
    }
  }, []);

  const loadFloorPlans = useCallback(async () => {
    try {
      const [fps, vs] = await Promise.all([getFloorPlans(), getVenues()]);
      setFloorPlans(fps);
      setVenues(vs);
    } catch {
      // planning API may not be authenticated — fail silently
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadFloorPlans(); }, [loadFloorPlans]);
  useEffect(() => {
    void loadVenuesFromApi().then(venues => {
      const names: string[] = [];
      for (const v of venues) {
        if (v.archived) continue;
        for (const fp of v.floorPlans) {
          for (const room of fp.rooms) {
            if (room.type !== 'table' && room.name) names.push(room.name);
          }
        }
      }
      setFpZoneNames(names);
    }).catch(() => {});
  }, []);

  const handleSyncFromFloorPlans = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { created, updated } = await syncTablesFromFloorPlans();
      await loadData();
      setSyncResult(`Synced: ${created} created, ${updated} updated from floor plan tables.`);
    } catch {
      setSyncResult('Sync failed — check API connection.');
    }
    setSyncing(false);
  };

  const handleFormChange = (field: keyof TableForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const startEdit = (t: EDTable) => {
    setEditingId(t.id);
    setForm({
      number: String(t.number),
      name: t.name ?? '',
      section: t.section ?? '',
      capacity: t.capacity != null ? String(t.capacity) : '',
      floor_plan_id: t.floor_plan_id != null ? String(t.floor_plan_id) : '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSaveTable = async () => {
    const num = Number(form.number);
    if (!form.number || isNaN(num) || num < 1) {
      showError('Table number is required.');
      return;
    }
    setSaving(true);
    try {
      const data = buildTablePayload(form, floorPlans.find(fp => String(fp.id) === form.floor_plan_id)?.venue_id ?? null);
      if (editingId !== null) {
        await eventDayApi.updateTable(editingId, data);
      } else {
        await eventDayApi.createTable(data);
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      await loadData();
      onSaved();
    } catch {
      showError('Failed to save table.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTable = async (id: string) => {
    setDeleting(id);
    try {
      await eventDayApi.deleteTable(id);
      await loadData();
      onSaved();
    } catch {
      showError('Failed to delete table.');
    } finally {
      setDeleting(null);
    }
  };

  const handleAddWaiter = async () => {
    if (!waiterForm.name.trim() || !waiterForm.pin.trim()) {
      showError('Name and PIN are required.');
      return;
    }
    setWaiterSaving(true);
    try {
      await eventDayApi.createWaiter({
        name: waiterForm.name.trim(),
        pin: waiterForm.pin.trim(),
        phone: waiterForm.phone.trim() || undefined,
        role: waiterForm.role,
      });
      setWaiterForm(EMPTY_WAITER_FORM);
      await loadData();
      onSaved();
    } catch {
      showError('Failed to add waiter.');
    } finally {
      setWaiterSaving(false);
    }
  };

  const handleDeleteWaiter = async (id: string) => {
    setWaiterDeleting(id);
    try {
      await eventDayApi.deleteWaiter(id);
      await loadData();
      onSaved();
    } catch (err) {
      const msg = (err as Error)?.message ?? '';
      if (msg.includes('foreign key') || msg.includes('waiter_sections') || msg.includes('violates')) {
        showError('Cannot delete — this staff member has table assignments. Remove their table assignments first.');
      } else {
        showError('Failed to delete staff member.');
      }
    } finally {
      setWaiterDeleting(null);
    }
  };

  const toggleWaiterTable = async (waiterId: string, tableId: string) => {
    const current = waiterTableMap[waiterId] ?? new Set<string>();
    const updated = new Set(current);
    if (updated.has(tableId)) {
      updated.delete(tableId);
    } else {
      updated.add(tableId);
    }
    setWaiterTableMap(prev => ({ ...prev, [waiterId]: updated }));
    setAssignSaving(waiterId);
    try {
      await eventDayApi.assignWaiterTables(waiterId, Array.from(updated));
      onSaved();
    } catch {
      setWaiterTableMap(prev => ({ ...prev, [waiterId]: current }));
      showError('Failed to update waiter assignment.');
    } finally {
      setAssignSaving(null);
    }
  };

  const handleAddFloorPlan = async () => {
    if (!fpForm.name.trim() || !fpForm.venue_id) {
      showError('Floor plan name and venue are required.');
      return;
    }
    setFpSaving(true);
    try {
      await createFloorPlan(fpForm.name.trim(), fpForm.venue_id, fpImageFile);
      setFpForm(EMPTY_FP_FORM);
      setFpImageFile(null);
      if (fpImageRef.current) fpImageRef.current.value = '';
      await loadFloorPlans();
    } catch {
      showError('Failed to create floor plan. Make sure you are logged in.');
    } finally {
      setFpSaving(false);
    }
  };

  const handleDeleteFloorPlan = async (id: string) => {
    setFpDeleting(id);
    try {
      await deleteFloorPlan(id);
      await loadFloorPlans();
      await loadData();
      onSaved();
    } catch {
      showError('Failed to delete floor plan.');
    } finally {
      setFpDeleting(null);
    }
  };

  // Zones from floor plan rooms (non-table rooms = drawn zones), loaded asynchronously from DB.
  const sections = Array.from(new Set([
    ...fpZoneNames,
    ...tables.map(t => t.section).filter(Boolean) as string[],
  ]));

  const getVenueName = (venueId: string) => venueNameFor(venues, venueId);

  // Group floor plans by venue for display
  const fpByVenue = groupFloorPlansByVenue(floorPlans);

  const content = (
    <div className={inline ? 'ed-setup-inline' : 'ed-setup-panel'}>
      {!inline && (
        <div className="ed-setup-header">
          <Settings size={16} color="var(--secondary)" />
          <h2>Event Day Setup</h2>
          <button className="ed-btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
      )}

      {loadError != null && <LoadErrorBox what="tables and staff" error={loadError} onRetry={loadData} />}

      {tabs.length > 1 && (
        <div className="ed-setup-tabs">
          {tabs.includes('tables') && (
            <button
              className={`ed-setup-tab ${activeTab === 'tables' ? 'active' : ''}`}
              onClick={() => setActiveTab('tables')}
            >
              Tables
            </button>
          )}
          {tabs.includes('floor-plans') && (
            <button
              className={`ed-setup-tab ${activeTab === 'floor-plans' ? 'active' : ''}`}
              onClick={() => setActiveTab('floor-plans')}
            >
              Floor Plans
            </button>
          )}
          {tabs.includes('waiters') && (
            <button
              className={`ed-setup-tab ${activeTab === 'waiters' ? 'active' : ''}`}
              onClick={() => setActiveTab('waiters')}
            >
              Waiters
            </button>
          )}
        </div>
      )}

      <div>

        {/* ── Tables tab ── */}
        {activeTab === 'tables' && (
          <>
            <div className="ed-tables-tab-header">
              <span className="ed-tables-tab-hint">
                Tables are drawn in Venues &amp; Floor Plans.
              </span>
              <button
                className="ed-btn-secondary ed-btn-sync"
                onClick={handleSyncFromFloorPlans}
                disabled={syncing}
              >
                {syncing ? 'Syncing…' : '↻ Sync from floor plans'}
              </button>
            </div>
            {syncResult && (
              <div className="ed-sync-result">
                {syncResult}
              </div>
            )}
            <div className="ed-table-form">
              <h4>{editingId !== null ? 'Edit Table' : 'Add Table'}</h4>
              <div className="ed-form-row">
                <div className="ed-form-field ed-form-field--table-number">
                  <label>No.</label>
                  <input
                    className="ed-form-input"
                    type="number"
                    min={1}
                    placeholder="1"
                    value={form.number}
                    onChange={e => handleFormChange('number', e.target.value)}
                  />
                </div>
                <div className="ed-form-field">
                  <label>Name (optional)</label>
                  <input
                    className="ed-form-input"
                    placeholder="e.g. Head Table"
                    value={form.name}
                    onChange={e => handleFormChange('name', e.target.value)}
                  />
                </div>
              </div>
              <div className="ed-form-row">
                <div className="ed-form-field">
                  <label>Section</label>
                  <input
                    className="ed-form-input"
                    placeholder="e.g. VIP, Family, General"
                    value={form.section}
                    onChange={e => handleFormChange('section', e.target.value)}
                    list="ed-section-list"
                  />
                  <datalist id="ed-section-list">
                    {sections.map(s => <option key={s} value={s} />)}
                  </datalist>
                </div>
                <div className="ed-form-field ed-form-field--table-capacity">
                  <label>Capacity</label>
                  <input
                    className="ed-form-input"
                    type="number"
                    min={1}
                    placeholder="8"
                    value={form.capacity}
                    onChange={e => handleFormChange('capacity', e.target.value)}
                  />
                </div>
              </div>
              {floorPlans.length > 0 && (
                <div className="ed-form-row">
                  <div className="ed-form-field">
                    <label>Floor Plan</label>
                    <SearchableSelect
                      className="ed-form-input"
                      value={form.floor_plan_id}
                      onChange={value => handleFormChange('floor_plan_id', value)}
                      disabled={!canLinkFloorPlans(floorPlans)}
                      placeholder="— None —"
                      options={[
                        { value: '', label: '— None —' },
                        ...floorPlans.map(fp => ({ value: String(fp.id), label: `${fp.name} (${getVenueName(fp.venue_id)})` })),
                      ]}
                    />
                    {!canLinkFloorPlans(floorPlans) && (
                      <p className="form-hint">You can't change the floor plan yet. An existing link is kept.</p>
                    )}
                  </div>
                </div>
              )}
              <div className="ed-table-form-actions">
                <button
                  className="ed-btn-add ed-btn-add--grow"
                  onClick={handleSaveTable}
                  disabled={saving}
                >
                  <Plus size={13} />
                  {saving ? 'Saving…' : editingId !== null ? 'Update Table' : 'Add Table'}
                </button>
                {editingId !== null && (
                  <button className="ed-btn-secondary" onClick={cancelEdit}>Cancel</button>
                )}
              </div>
            </div>

            {tables.length === 0 ? (
              <EmptyState compact title="No tables yet" description="Add one above." />
            ) : (
              <DataTable
                columns={[
                  { key: 'number', header: '#', width: '48px', render: (_v, t) => `T${t.number}` },
                  { key: 'name', header: 'Name', render: (_v, t) => t.name || `Table ${t.number}` },
                  { key: 'section', header: 'Section', render: (_v, t) => t.section || '—' },
                  { key: 'capacity', header: 'Seats', render: (_v, t) => t.capacity ?? '—' },
                  { key: 'floor_plan_name', header: 'Floor Plan', render: (_v, t) => t.floor_plan_name || '—' },
                  {
                    key: 'actions',
                    header: '',
                    align: 'right',
                    render: (_v, t) => (
                      <div className="ed-table-row-actions">
                        <button
                          className="ed-btn-secondary ed-btn-edit-compact"
                          onClick={() => startEdit(t)}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          className="ed-btn-remove"
                          onClick={() => handleDeleteTable(t.id)}
                          disabled={deleting === t.id}
                          title="Delete"
                        >
                          {deleting === t.id ? '…' : <Trash2 size={14} />}
                        </button>
                      </div>
                    ),
                  },
                ] as Column<EDTable>[]}
                data={tables}
                hoverable
                compact
              />
            )}
          </>
        )}

        {/* ── Floor Plans tab ── */}
        {activeTab === 'floor-plans' && (
          <>
            <div className="ed-table-form">
              <h4>Add Floor Plan</h4>
              {venues.length === 0 ? (
                <p className="ed-fp-empty-hint">
                  No venues found. Add venues in the Vendors section first.
                </p>
              ) : (
                <>
                  <div className="ed-form-row">
                    <div className="ed-form-field">
                      <label>Name</label>
                      <input
                        className="ed-form-input"
                        placeholder="e.g. Main Ballroom"
                        value={fpForm.name}
                        onChange={e => setFpForm(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div className="ed-form-field">
                      <label>Venue</label>
                      <SearchableSelect
                        className="ed-form-input"
                        value={fpForm.venue_id}
                        onChange={value => setFpForm(prev => ({ ...prev, venue_id: value }))}
                        options={venues.map(v => ({ value: String(v.id), label: v.name }))}
                        placeholder="Select venue"
                      />
                    </div>
                  </div>
                  <div className="ed-form-row">
                    <div className="ed-form-field">
                      <label>Floor plan image (optional)</label>
                      <div className="ed-file-input-row">
                        <input
                          ref={fpImageRef}
                          type="file"
                          accept="image/*"
                          className="u-hidden"
                          onChange={e => setFpImageFile(e.target.files?.[0] ?? null)}
                        />
                        <button
                          className="ed-btn-secondary"
                          type="button"
                          onClick={() => fpImageRef.current?.click()}
                        >
                          <Image size={13} />
                          {fpImageFile ? fpImageFile.name : 'Choose image'}
                        </button>
                        {fpImageFile && (
                          <button
                            className="ed-btn-remove"
                            type="button"
                            onClick={() => { setFpImageFile(null); if (fpImageRef.current) fpImageRef.current.value = ''; }}
                          >
                            <X size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    className="ed-btn-add ed-btn-add--full"
                    onClick={handleAddFloorPlan}
                    disabled={fpSaving}
                  >
                    <Plus size={13} />
                    {fpSaving ? 'Saving…' : 'Add Floor Plan'}
                  </button>
                </>
              )}
            </div>

            {Object.keys(fpByVenue).length === 0 ? (
              <EmptyState compact title="No floor plans yet" description="Add one above." />
            ) : (
              Object.entries(fpByVenue).map(([venueId, fps]) => (
                <div key={venueId} className="ed-fp-venue-group">
                  <div className="ed-fp-venue-label">{venueNameFor(venues, venueId)}</div>
                  {fps.map(fp => (
                    <div key={fp.id} className="ed-fp-row">
                      {fp.image_url ? (
                        <img
                          className="ed-fp-thumb"
                          src={resolveMediaUrl(fp.image_url)}
                          alt={fp.name}
                        />
                      ) : (
                        <div className="ed-fp-thumb ed-fp-thumb--empty">
                          <Image size={16} color="var(--text-muted)" />
                        </div>
                      )}
                      <div className="ed-fp-info">
                        <div className="ed-fp-name">{fp.name}</div>
                        <div className="ed-fp-meta">
                          {tables.filter(t => String(t.floor_plan_id) === fp.id).length} tables assigned
                        </div>
                      </div>
                      <button
                        className="ed-btn-remove"
                        onClick={() => handleDeleteFloorPlan(fp.id)}
                        disabled={fpDeleting === fp.id}
                        title="Delete floor plan"
                      >
                        {fpDeleting === fp.id ? '…' : <Trash2 size={14} />}
                      </button>
                    </div>
                  ))}
                </div>
              ))
            )}
          </>
        )}

        {/* ── Waiters tab ── */}
        {activeTab === 'waiters' && (
          <>
            {/* Add Staff form */}
            <div className="ed-waiter-add-form">
              <input
                className="ed-waiter-add-input"
                placeholder="Name *"
                value={waiterForm.name}
                onChange={e => setWaiterForm(f => ({ ...f, name: e.target.value }))}
              />
              <input
                className="ed-waiter-add-input"
                placeholder="PIN *"
                value={waiterForm.pin}
                onChange={e => setWaiterForm(f => ({ ...f, pin: e.target.value }))}
              />
              <input
                className="ed-waiter-add-input"
                placeholder="Phone"
                value={waiterForm.phone}
                onChange={e => setWaiterForm(f => ({ ...f, phone: e.target.value }))}
              />
              <SearchableSelect
                className="form-select"
                value={waiterForm.role}
                onChange={value => setWaiterForm(f => ({ ...f, role: value as WaiterForm['role'] }))}
                options={[
                  { value: 'waiter', label: 'Waiter' },
                  { value: 'usher', label: 'Usher' },
                  { value: 'bouncer', label: 'Bouncer' },
                  { value: 'other', label: 'Other' },
                ]}
              />
              <button
                className="ed-btn-add"
                onClick={handleAddWaiter}
                disabled={waiterSaving}
              >
                {waiterSaving ? '…' : '+ Add Staff'}
              </button>
            </div>
            {waiters.length === 0 ? (
              <EmptyState compact title="No staff yet" description="Add your first staff member above." />
            ) : (
              waiters.map(w => {
                const assigned = waiterTableMap[w.id] ?? new Set<string>();
                const allSections = Array.from(new Set(tables.map(t => t.section).filter(Boolean))) as string[];
                const assignedTables = tables.filter(t => assigned.has(t.id));
                const assignedSections = Array.from(new Set(assignedTables.map(t => t.section).filter(Boolean))) as string[];

                const handleTableSelect = async (tableId: string, checked: boolean) => {
                  const current = waiterTableMap[w.id] ?? new Set<string>();
                  const next = new Set(current);
                  if (checked && next.size >= 10) return;
                  checked ? next.add(tableId) : next.delete(tableId);
                  setWaiterTableMap(prev => ({ ...prev, [w.id]: next }));
                  setAssignSaving(w.id);
                  try {
                    await eventDayApi.assignWaiterTables(w.id, Array.from(next));
                  } catch {
                    setWaiterTableMap(prev => ({ ...prev, [w.id]: current }));
                  } finally {
                    setAssignSaving(null);
                  }
                };

                const handleSectionSelect = async (section: string, checked: boolean) => {
                  const current = waiterTableMap[w.id] ?? new Set<string>();
                  const sectionTables = tables.filter(t => t.section === section);
                  const currentSections = Array.from(new Set(tables.filter(t => current.has(t.id)).map(t => t.section).filter(Boolean)));
                  if (checked && currentSections.length >= 5 && !currentSections.includes(section)) return;
                  const next = new Set(current);
                  sectionTables.forEach(t => checked ? next.add(t.id) : next.delete(t.id));
                  setWaiterTableMap(prev => ({ ...prev, [w.id]: next }));
                  setAssignSaving(w.id);
                  try {
                    await eventDayApi.assignWaiterTables(w.id, Array.from(next));
                  } catch {
                    setWaiterTableMap(prev => ({ ...prev, [w.id]: current }));
                  } finally {
                    setAssignSaving(null);
                  }
                };

                return (
                  <div key={w.id} className="ed-waiter-assign-card">
                    <div className="ed-waiter-assign-header">
                      <div className="ed-waiter-assign-name">
                        <span className="ed-waiter-assign-avatar">{w.name.charAt(0).toUpperCase()}</span>
                        <div>
                          <span>{w.name}</span>
                          {w.role && w.role !== 'waiter' && (
                            <span className="ed-waiter-role-badge">{w.role}</span>
                          )}
                          <div className="ed-waiter-assign-summary">
                            {assigned.size} table{assigned.size !== 1 ? 's' : ''} assigned
                          </div>
                        </div>
                      </div>
                      <div className="ed-waiter-actions">
                        {assignSaving === w.id && (
                          <span className="ed-waiter-saving-hint">saving…</span>
                        )}
                        <button
                          className="ed-btn-remove"
                          onClick={() => handleDeleteWaiter(w.id)}
                          disabled={waiterDeleting === w.id}
                          title="Remove staff member"
                        >
                          {waiterDeleting === w.id ? '…' : <Trash2 size={14} />}
                        </button>
                      </div>
                    </div>

                    {tables.length === 0 ? (
                      <p className="ed-waiter-empty-hint">Add tables first.</p>
                    ) : (
                      <div className="ed-waiter-assign-selects">
                        {allSections.length > 0 && (
                          <div className="ed-assign-field">
                            <label className="ed-assign-label">Sections</label>
                            <TagMultiSelect
                              values={assignedSections}
                              options={allSections}
                              onChange={async newSections => {
                                const current = waiterTableMap[w.id] ?? new Set<string>();
                                const next = new Set(current);
                                for (const sec of assignedSections) {
                                  if (!newSections.includes(sec)) {
                                    tables.filter(t => t.section === sec).forEach(t => next.delete(t.id));
                                  }
                                }
                                for (const sec of newSections) {
                                  if (!assignedSections.includes(sec)) {
                                    tables.filter(t => t.section === sec).forEach(t => next.add(t.id));
                                  }
                                }
                                setWaiterTableMap(prev => ({ ...prev, [w.id]: next }));
                                setAssignSaving(w.id);
                                try {
                                  await eventDayApi.assignWaiterTables(w.id, Array.from(next));
                                } catch {
                                  setWaiterTableMap(prev => ({ ...prev, [w.id]: current }));
                                } finally {
                                  setAssignSaving(null);
                                }
                              }}
                              allowCreate={false}
                              placeholder="Select sections…"
                            />
                          </div>
                        )}
                        <div className="ed-assign-field">
                          <label className="ed-assign-label">Tables</label>
                          <TagMultiSelect
                            values={Array.from(assigned).map(id => {
                              const t = tables.find(t => t.id === id);
                              return t ? `T${t.number}${t.name ? ` ${t.name}` : ''}${t.section ? ` · ${t.section}` : ''}` : String(id);
                            })}
                            options={tables.map(t => `T${t.number}${t.name ? ` ${t.name}` : ''}${t.section ? ` · ${t.section}` : ''}`)}
                            onChange={async labels => {
                              const selectedIds = new Set(
                                labels
                                  .map(label => tables.find(t => `T${t.number}${t.name ? ` ${t.name}` : ''}${t.section ? ` · ${t.section}` : ''}` === label)?.id)
                                  .filter((id): id is string => id !== undefined)
                              );
                              const current = waiterTableMap[w.id] ?? new Set<string>();
                              setWaiterTableMap(prev => ({ ...prev, [w.id]: selectedIds }));
                              setAssignSaving(w.id);
                              try {
                                await eventDayApi.assignWaiterTables(w.id, Array.from(selectedIds));
                              } catch {
                                setWaiterTableMap(prev => ({ ...prev, [w.id]: current }));
                              } finally {
                                setAssignSaving(null);
                              }
                            }}
                            allowCreate={false}
                            placeholder="Select tables…"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );

  if (inline) return content;

  return (
    <div className="ed-setup-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      {content}
    </div>
  );
};

export default TableSetupModal;
